import { useQuery } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useAtom } from 'jotai';
import { useEffect } from 'react';

import useHandleError from '@/hooks/useHandleError';
import { selectedOrganizationAtom } from '@/pages/Organizations';
import { axiosClient } from '@/services/BaseApi.service';
import type { CasesMetricsGroupByDTO } from './DashboardCases.types';

export const useFetchCaseMetrics = (
  params: Record<string, unknown>,
  setValue?: (value: CasesMetricsGroupByDTO[]) => void
) => {
  const [selectedOrganization] = useAtom(selectedOrganizationAtom);
  const handleError = useHandleError();

  const body = { ...params };

  if (
    selectedOrganization.label &&
    selectedOrganization.label !== 'all' &&
    !window.location.pathname.includes('reports')
  ) {
    body.organization_label = selectedOrganization.label;
  }

  const queryFn = () => {
    return axiosClient
      .post('v2/cases/case_metrics/group_by', body)
      .then((resp) => resp.data);
  };

  const { data, isError, error, isFetching, isSuccess } = useQuery<
    CasesMetricsGroupByDTO[],
    AxiosError
  >({
    queryKey: ['cases/case_metrics/group_by', body],
    staleTime: Infinity,
    queryFn
  });

  useEffect(() => {
    if (isError) {
      handleError(error, 'Failure to fetch cases by group by field.');
    }
  }, [isError]);

  useEffect(() => {
    if (isSuccess && setValue) {
      setValue(data);
    }
  }, [data, isSuccess, setValue]);

  return {
    data: data || [],
    isFetching
  };
};
